"use client";

import React, { useEffect, useState } from "react";
import {
  ArrowLeft,
  Eye,
  EyeOff,
  Copy,
  Check,
  Search,
  Loader2,
} from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import { Input } from "@/shadcn-components/ui/input";
import { Card, CardContent } from "@/shadcn-components/ui/card";
import { Label } from "@/shadcn-components/ui/label";
import { Switch } from "@/shadcn-components/ui/switch";
import { Separator } from "@/shadcn-components/ui/separator";
import { Alert, AlertDescription } from "@/shadcn-components/ui/alert";
import { BrokerLogo, GatewayChip } from "@/components/common";
import { useAccountsStore, getGatewayLabel } from "@/lib/accounts/store";
import { TradingAccount } from "@/lib/accounts/types";
import {
  useGatewaysStore,
  useGatewayMeta,
  useGatewayAddressHealth,
  accountHome,
  findEndpointIndex,
  findGatewayByHubUrl,
  hubScheme,
  CUSTOM_GATEWAY_SLUG_PREFIX,
} from "@/lib/gateways";
import { GatewayAddressField } from "./GatewayAddressField";
import { useConnectionProgressStore } from "@/vybe-trader-client/connection-progress/stores/useConnectionProgressStore";
import { VybeTraderClient } from "@/vybe-trader-client/connection/client";

interface EditAccountContentProps {
  account: TradingAccount;
  onBack: () => void;
}

type ResolveState =
  | { kind: "idle" }
  | { kind: "resolving" }
  | { kind: "done"; ip: string; port: number; latencyMs: number }
  | { kind: "error"; message: string };

/** Full-page Edit Account view (proposal 19): summary card, read-only connection
 * group with endpoint lookup, Add-flow-styled credentials, save + delete. */
export function EditAccountContent({ account, onBack }: EditAccountContentProps) {
  const updateAccount = useAccountsStore((state) => state.updateAccount);
  const deleteAccount = useAccountsStore((state) => state.deleteAccount);
  const activeAccountId = useAccountsStore((state) => state.activeAccountId);
  const isConnected = useConnectionProgressStore((state) => state.isConnected);

  const [login, setLogin] = useState(account.credentials.login);
  const [password, setPassword] = useState(account.credentials.password);
  const [showPassword, setShowPassword] = useState(false);
  const [savePassword, setSavePassword] = useState(Boolean(account.credentials.password));
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [resolveState, setResolveState] = useState<ResolveState>({ kind: "idle" });

  // ── Several gateways behind one card: let the trader switch address ────────
  // The account's card (by slug, else by the hub it is saved against). When that gateway reports
  // `endpointsAreGateways`, its endpoints are the broker's gateway addresses and the account may be
  // moved between them; for every other gateway (MT5, custom) none of this renders.
  const gateways = useGatewaysStore((state) => state.gateways);
  const isCustomGateway = Boolean(account.gatewaySlug?.startsWith(CUSTOM_GATEWAY_SLUG_PREFIX));
  const card = isCustomGateway
    ? undefined
    : gateways.find((g) => g.slug === account.gatewaySlug) ??
      findGatewayByHubUrl(gateways, account.hubUrl);
  const gatewayMeta = useGatewayMeta(card?.hubUrl ?? null);
  const addresses = gatewayMeta.endpointsAreGateways ? gatewayMeta.endpoints : [];
  const { health: addressHealth } = useGatewayAddressHealth(
    addresses,
    hubScheme(card?.hubUrl),
    addresses.length > 0
  );
  const savedAddressIdx = findEndpointIndex(
    addresses,
    account.host ?? account.lastConnectedHost,
    account.port ?? account.lastConnectedPort
  );
  /** The address the trader picked in this view; null = keep the saved one. */
  const [pickedAddressIdx, setPickedAddressIdx] = useState<number | null>(null);
  const addressIdx = pickedAddressIdx ?? savedAddressIdx;
  const addressChanged =
    pickedAddressIdx !== null && pickedAddressIdx !== savedAddressIdx && Boolean(addresses[pickedAddressIdx]);

  /**
   * Re-seed the editable fields when a DIFFERENT account is opened.
   *
   * These are `useState(account…)`, which runs only on mount — and the desktop accounts sheet keeps
   * `currentView === "edit"` while swapping `editAccount`, so the component is reused and the inputs
   * kept the previous account's credentials. The header renders the live prop, so the card showed
   * account 29 over another account's login/password (SR Global, 2026-09-09: account 29 displaying the
   * FxPro login 591673510). Saving that would have written those credentials onto account 29.
   * Keyed on account.id so a genuine edit of the CURRENT account is never clobbered mid-typing.
   */
  useEffect(() => {
    setLogin(account.credentials.login);
    setPassword(account.credentials.password);
    setSavePassword(Boolean(account.credentials.password));
    setShowPassword(false);
    setSaved(false);
    setConfirmDelete(false);
    setResolveState({ kind: "idle" });
    setPickedAddressIdx(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account.id]);

  const host = account.host ?? account.lastConnectedHost;
  const port = account.port ?? account.lastConnectedPort ?? 443;
  const isActiveAndConnected = account.id === activeAccountId && isConnected;
  const canResolve = Boolean(host) && isConnected;
  const passwordNotSaved = !account.credentials.password;

  const dirty =
    login.trim() !== account.credentials.login ||
    (savePassword ? password : "") !== account.credentials.password ||
    addressChanged;

  const handleCopy = async () => {
    if (!password) return;
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  const handleSave = () => {
    // Switching gateway address re-points the account at the chosen instance of the same broker.
    const home =
      addressChanged && card && pickedAddressIdx !== null
        ? accountHome(card, addresses[pickedAddressIdx], true)
        : null;
    updateAccount({
      ...account,
      ...(home ?? {}),
      credentials: {
        login: login.trim() || account.credentials.login,
        password: savePassword ? password : "",
      },
    });
    if (home) {
      setPickedAddressIdx(null);
      // The live session is on the old address — re-dial so the switch takes effect now.
      if (account.id === activeAccountId) useAccountsStore.getState().requestReconnect();
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };

  const handleDelete = () => {
    deleteAccount(account.id);
    onBack();
  };

  const handleResolve = async () => {
    if (!host || resolveState.kind === "resolving") return;
    setResolveState({ kind: "resolving" });
    try {
      const result = await VybeTraderClient.getInstance().resolveEndpoint(host, port);
      if (result.success && result.ip) {
        setResolveState({
          kind: "done",
          ip: result.ip,
          port: result.port,
          latencyMs: result.latencyMs,
        });
      } else {
        setResolveState({ kind: "error", message: result.errorMessage || "unreachable" });
      }
    } catch {
      setResolveState({ kind: "error", message: "unreachable" });
    }
  };

  const formatDate = (ts: number) =>
    new Date(ts).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  return (
    <div className="flex flex-col h-full bg-background text-foreground">
      {/* Back-arrow header — same pattern as other mobile subscreens */}
      <div className="flex items-center gap-3 px-4 py-3 h-14 border-b border-border">
        <Button variant="ghost" size="icon" onClick={onBack} className="h-8 w-8 shrink-0">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-base font-semibold">Edit Account</h1>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-3 space-y-3">
        {/* Account summary card — Add-flow company-card idiom */}
        <div className="rounded-xl border border-border bg-card p-3 flex items-center gap-3">
          <BrokerLogo
            companyName={account.companyName}
            logoUrl={account.brokerLogoUrl}
            className="w-9 h-9"
            logoClassName="w-6 h-6"
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="font-medium text-sm truncate">
                {account.credentials.login}
              </span>
              <span
                className={`w-[7px] h-[7px] rounded-full shrink-0 ${
                  isActiveAndConnected ? "bg-green-500" : "bg-red-500"
                }`}
                aria-label={isActiveAndConnected ? "Connected" : "Disconnected"}
              />
            </div>
            <div className="text-xs text-muted-foreground truncate mt-0.5">
              {account.companyName} · {account.serverName}
            </div>
          </div>
          <GatewayChip label={getGatewayLabel(account)} />
        </div>

        {/* Connection details (read-only) */}
        <div>
          <h3 className="text-muted-foreground text-xs uppercase font-medium tracking-wider mb-2 px-0.5">
            Connection · read-only
          </h3>
          <Card>
            <CardContent className="p-0 text-sm">
              <Row label="Gateway" value={getGatewayLabel(account)} mono />
              <Separator />
              <Row label="Server" value={account.serverName} />
              <Separator />
              <Row label="Endpoint" value={host ? `${host}:${port}` : "—"} mono />
              <Separator />
              <div className="flex items-center justify-between gap-3 p-3.5">
                <span className="text-sm font-medium text-muted-foreground shrink-0">
                  Resolved
                </span>
                <span className="flex items-center gap-2 min-w-0">
                  <span className="font-mono text-[13px] text-muted-foreground truncate">
                    {resolveState.kind === "idle" &&
                      (canResolve ? "tap to look up" : "gateway offline")}
                    {resolveState.kind === "resolving" && "resolving…"}
                    {resolveState.kind === "done" && (
                      <span className="text-foreground">
                        {resolveState.ip}:{resolveState.port} · {resolveState.latencyMs} ms
                      </span>
                    )}
                    {resolveState.kind === "error" && "unreachable"}
                  </span>
                  <button
                    type="button"
                    onClick={handleResolve}
                    disabled={!canResolve || resolveState.kind === "resolving"}
                    className="text-muted-foreground hover:text-foreground shrink-0 disabled:opacity-40"
                    aria-label="Resolve endpoint"
                  >
                    {resolveState.kind === "resolving" ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Search className="h-4 w-4" />
                    )}
                  </button>
                </span>
              </div>
              <Separator />
              <Row label="Last used" value={formatDate(account.lastConnectedAt)} />
            </CardContent>
          </Card>
        </div>

        {/* Editable credentials — same field styling as the Add Account form */}
        <div>
          <h3 className="text-muted-foreground text-xs uppercase font-medium tracking-wider mb-2 px-0.5">
            Credentials
          </h3>
          <Card>
            <CardContent className="p-0">
              {/* Gateway address — only for a broker that runs several gateways */}
              {addresses.length > 0 && (
                <>
                  <GatewayAddressField
                    endpoints={addresses}
                    health={addressHealth}
                    selectedIndex={addressIdx}
                    onSelect={setPickedAddressIdx}
                  />
                  <Separator />
                </>
              )}
              <div className="flex items-center justify-between p-3.5">
                <Label htmlFor="edit-login" className="text-sm font-medium">Login</Label>
                <Input
                  id="edit-login"
                  value={login}
                  onChange={(e) => setLogin(e.target.value)}
                  className="w-44 text-right border-none bg-transparent p-0 focus-visible:ring-0"
                />
              </div>
              <Separator />
              <div className="flex items-center justify-between p-3.5 gap-2">
                <Label htmlFor="edit-password" className="text-sm font-medium">Password</Label>
                <div className="flex items-center gap-1.5 w-48 justify-end">
                  <Input
                    id="edit-password"
                    type={showPassword ? "text" : "password"}
                    placeholder={
                      passwordNotSaved && !password ? "not saved" : "not set"
                    }
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={!savePassword}
                    className="text-right border-none bg-transparent p-0 focus-visible:ring-0 disabled:opacity-60"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="text-muted-foreground hover:text-foreground shrink-0"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                  <button
                    type="button"
                    onClick={handleCopy}
                    disabled={!password}
                    className="text-muted-foreground hover:text-foreground shrink-0 disabled:opacity-40"
                    aria-label="Copy password"
                  >
                    {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              {passwordNotSaved && (
                <p className="px-3.5 pb-2 -mt-1.5 text-[11px] text-muted-foreground">
                  No password is saved on this device.
                </p>
              )}
              <Separator />
              <div className="flex items-center justify-between p-3.5">
                <div className="space-y-0.5">
                  <Label htmlFor="edit-save" className="text-sm font-medium">Save password</Label>
                  <p className="text-[11px] text-muted-foreground">Stored locally on this device only.</p>
                </div>
                <Switch id="edit-save" checked={savePassword} onCheckedChange={setSavePassword} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Delete confirmation */}
        {confirmDelete && (
          <Alert className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950">
            <AlertDescription>
              <p className="text-sm mb-3">
                Are you sure you want to delete this account?
              </p>
              <div className="flex gap-2">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDelete}
                  className="flex-1"
                >
                  Delete
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmDelete(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}
      </div>

      <div className="px-5 py-3 border-t border-border space-y-2">
        <Button
          onClick={handleSave}
          disabled={!dirty && !saved}
          className="w-full h-10 text-sm font-medium"
        >
          {saved ? (
            <span className="flex items-center gap-2"><Check className="h-4 w-4" /> Saved</span>
          ) : (
            "Save changes"
          )}
        </Button>
        <Button
          variant="outline"
          onClick={() => setConfirmDelete(true)}
          className="w-full h-10 text-sm font-medium text-red-400 border-red-500/40 hover:bg-red-500/10 hover:text-red-400"
        >
          Delete account
        </Button>
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3 p-3.5">
      <span className="text-sm font-medium text-muted-foreground shrink-0">{label}</span>
      <span
        className={`min-w-0 text-right ${mono ? "font-mono text-[13px]" : "text-sm"} text-foreground truncate`}
      >
        {value}
      </span>
    </div>
  );
}
